import { z } from "zod";

export const opsDatasyncRunSchema = z.object({
  id: z.number().or(z.string()),
  task_type: z.string().optional().default(""),
  task_label: z.string().nullable().optional(),
  scope_type: z.string().optional().default(""),
  status: z.string().optional().default(""),
  summary_text: z.string().nullable().optional(),
  trigger_mode: z.string().nullable().optional(),
  requested_by_label: z.string().nullable().optional(),
  queued_at: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  progress_pct: z.number().nullable().optional(),
});

export const opsDatasyncRunStepSchema = z.object({
  id: z.number().or(z.string()),
  run_id: z.number().or(z.string()).optional(),
  step_key: z.string().optional().default(""),
  step_label: z.string().nullable().optional(),
  status: z.string().optional().default(""),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  progress_pct: z.number().nullable().optional(),
  summary_text: z.string().nullable().optional(),
  error_msg: z.string().nullable().optional(),
});

export const opsDatasyncScheduleSchema = z.object({
  id: z.number().or(z.string()),
  name: z.string().optional().default(""),
  task_type: z.string().optional().default(""),
  scope_type: z.string().optional().default(""),
  status: z.string().optional().default(""),
  schedule_kind: z.string().optional().default(""),
  schedule_config_json: z.record(z.any()).optional().default({}),
  scope_json: z.record(z.any()).optional().default({}),
  updated_at: z.string().nullable().optional(),
  next_run_time: z.string().nullable().optional(),
  last_run_time: z.string().nullable().optional(),
});

export const opsDatasyncBootstrapSchema = z.object({
  permissions: z.object({ can_manage: z.boolean().optional().default(false) }).optional().default({ can_manage: false }),
  task_types: z
    .array(
      z.object({
        key: z.string(),
        label: z.string().nullable().optional(),
        kind: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  schedule_kinds: z
    .array(
      z.object({
        key: z.string(),
        label: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  universes: z
    .array(
      z.object({
        key: z.string(),
        label: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
  runs: z.array(opsDatasyncRunSchema).optional().default([]),
  schedules: z.array(opsDatasyncScheduleSchema).optional().default([]),
  server_time: z.string().optional().default(""),
});

export const opsDatasyncRunLogItemSchema = z.object({
  id: z.number().or(z.string()).optional(),
  ts: z.string().nullable().optional(),
  level: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});

export const opsDatasyncRunLogsSchema = z.object({
  run_id: z.number().or(z.string()).optional(),
  cursor: z.number().optional().default(0),
  next_cursor: z.number().optional().default(0),
  items: z.array(opsDatasyncRunLogItemSchema).optional().default([]),
});

export const opsDataStatusSchema = z.object({
  days: z.number().optional().default(0),
  expected_count: z.number().optional().default(0),
  rows: z.array(z.record(z.any())).optional().default([]),
  overview: z.record(z.any()).optional().default({}),
});

export const opsUpdateLogsSchema = z.object({
  limit: z.number().optional().default(0),
  update_logs: z.array(z.record(z.any())).optional().default([]),
  quality_runs: z.array(z.record(z.any())).optional().default([]),
});

export const opsSummarySchema = z.record(z.any());

export const opsTimelineSchema = z.object({
  hours: z.number().optional().default(24),
  limit: z.number().optional().default(120),
  rows: z.array(z.record(z.any())).optional().default([]),
  generated_at: z.string().optional().default(""),
});

export const opsFreshnessSchema = z.object({
  scope: z.string().optional().default("core"),
  rows: z.array(z.record(z.any())).optional().default([]),
  generated_at: z.string().optional().default(""),
});

export const opsSourceHealthSchema = z.object({
  hours: z.number().optional().default(24),
  rows: z.array(z.record(z.any())).optional().default([]),
  generated_at: z.string().optional().default(""),
});

export const opsAlertsSchema = z.object({
  limit: z.number().optional().default(0),
  status: z.string().optional().default("OPEN"),
  rows: z.array(z.record(z.any())).optional().default([]),
});

export const opsDashboardSchema = z.object({
  status_data: opsDataStatusSchema.optional().default({ days: 0, expected_count: 0, rows: [], overview: {} }),
  logs_data: opsUpdateLogsSchema.optional().default({ limit: 0, update_logs: [], quality_runs: [] }),
  summary_data: opsSummarySchema.optional().default({}),
  timeline_data: opsTimelineSchema.optional().default({ hours: 24, limit: 120, rows: [], generated_at: "" }),
  freshness_data: opsFreshnessSchema.optional().default({ scope: "core", rows: [], generated_at: "" }),
  source_health_data: opsSourceHealthSchema.optional().default({ hours: 24, rows: [], generated_at: "" }),
  alerts_data: opsAlertsSchema.optional().default({ limit: 0, status: "OPEN", rows: [] }),
  generated_at: z.string().optional().default(""),
});

export const quantPanelRunSchema = z.object({
  id: z.number().or(z.string()),
  status: z.string().optional().default(""),
  result_count: z.number().nullable().optional(),
  pick_date: z.string().nullable().optional(),
  trade_date: z.string().nullable().optional(),
  queued_at: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
  error_msg: z.string().nullable().optional(),
  export_csv_url: z.string().nullable().optional(),
  export_csv_file_name: z.string().nullable().optional(),
});

export const quantPanelPresetSchema = z.object({
  key: z.string(),
  label: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  target_count_hint: z.number().nullable().optional(),
  params: z.record(z.any()).optional().default({}),
});

export const quantPanelBootstrapSchema = z.object({
  defaults: z.record(z.any()).optional().default({}),
  presets: z.array(quantPanelPresetSchema).optional().default([]),
  runs: z.array(quantPanelRunSchema).optional().default([]),
  freshness: z.record(z.any()).nullable().optional(),
  poll_interval_seconds: z.number().optional().default(2),
});

export const quantPanelRunDetailSchema = quantPanelRunSchema.extend({
  params: z.record(z.any()).optional().default({}),
});

export const quantPanelRunLogsSchema = z.object({
  run_id: z.number().or(z.string()).optional(),
  cursor: z.number().optional().default(0),
  next_cursor: z.number().optional().default(0),
  items: z
    .array(
      z.object({
        id: z.number().or(z.string()).optional(),
        ts: z.string().nullable().optional(),
        level: z.string().nullable().optional(),
        message: z.string().nullable().optional(),
      }),
    )
    .optional()
    .default([]),
});

export const quantPanelSelectSchema = z.object({
  ts_code: z.string(),
  stock_name: z.string().nullable().optional(),
  stage_tag: z.string().nullable().optional(),
  stage_name: z.string().nullable().optional(),
  wyckoff_phase: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  reason_text: z.string().nullable().optional(),
});

export const quantPanelRunSelectsSchema = z.object({
  run: quantPanelRunSchema.nullable().optional(),
  total: z.number().optional().default(0),
  items: z.array(quantPanelSelectSchema).optional().default([]),
});

export const quantSelectsSchema = z.object({
  pick_date: z.string().nullable().optional(),
  trade_date: z.string().nullable().optional(),
  total: z.number().optional().default(0),
  items: z.array(quantPanelSelectSchema).optional().default([]),
});

export const newsFeedItemSchema = z.object({
  path: z.string(),
  url: z.string().optional().default(""),
  title: z.string(),
  summary: z.string().optional().default(""),
  published_at: z.string().optional().default(""),
  published_display: z.string().optional().default(""),
  category: z.string().optional().default(""),
  tags_display: z.string().optional().default(""),
  tags: z.array(z.string()).optional().default([]),
});

export const newsFeedSchema = z.object({
  items: z.array(newsFeedItemSchema).optional().default([]),
  total: z.number().optional().default(0),
  limit: z.number().optional().default(40),
  offset: z.number().optional().default(0),
  has_more: z.boolean().optional().default(false),
  categories: z.array(z.string()).optional().default([]),
});

export const newsArticleSchema = newsFeedItemSchema.extend({
  content_html: z.string().optional().default(""),
});

export const newsMetaUpdateSchema = z.object({
  updated: z.boolean().optional().default(false),
  article: newsArticleSchema.nullable().optional(),
});
